// Barrera de error de React: captura excepciones lanzadas durante el render de
// cualquier vista hija y muestra un aviso recuperable en lugar de dejar la PWA
// en blanco (React desmonta todo el árbol si un render lanza y no hay boundary).
//
// Se resetea al cambiar de ruta (via `resetKey`), de modo que salir de la vista
// que falló limpia el estado de error.

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { i18n } from '../i18n/index.js';

interface Props {
  children: ReactNode;
  /** Al cambiar este valor (p. ej. la ruta), se limpia el estado de error. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log para diagnóstico (visible en la consola del navegador).
    console.error('ErrorBoundary capturó un error de render:', error, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    // Al navegar a otra ruta, limpiar el error para reintentar el render.
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    if (this.state.error) {
      // Al ser un class component no puede usar el hook useTranslation; usa la
      // instancia i18n de forma imperativa (i18n.t). El aviso es puntual y se
      // limpia al navegar, así que no requiere re-render reactivo por idioma.
      return (
        <div className="flex flex-col items-center justify-center gap-space-md px-margin-mobile py-space-xl text-center">
          <span className="w-20 h-20 rounded-full bg-priority-urgent/15 border border-priority-urgent/40 flex items-center justify-center">
            <span className="material-symbols-outlined text-priority-urgent text-[40px]">error</span>
          </span>
          <div className="flex flex-col gap-space-xs">
            <span className="font-badge-micro text-badge-micro text-priority-urgent uppercase tracking-widest">
              {i18n.t('errorBoundary.badge')}
            </span>
            <h1 className="font-headline-lg-mobile text-headline-lg-mobile font-bold text-on-surface">
              {i18n.t('errorBoundary.title')}
            </h1>
            <p className="font-body-sm text-body-sm text-on-surface-variant max-w-xs">
              {i18n.t('errorBoundary.body')}
            </p>
          </div>
          <div className="flex items-center gap-space-sm">
            <button className="btn-secondary" type="button" onClick={() => this.setState({ error: null })}>
              <span className="material-symbols-outlined text-[18px]">refresh</span> {i18n.t('errorBoundary.retry')}
            </button>
            <a href="/" className="btn-primary">
              <span className="material-symbols-outlined text-[18px]">arrow_back</span>{' '}
              {i18n.t('errorBoundary.goCatalog')}
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
